/**
 * Puerto Rico マップ設定
 * Puerto Rico Solo Play 専用の設定値を定義
 */

import type { MapConfig, TileType } from '../../../src/types';

// ============================================================================
// マップ基本設定
// ============================================================================

/**
 * Puerto Rico マップ設定
 */
export const PUERTO_RICO_MAP_CONFIG: MapConfig = {
  name: 'Puerto Rico',
  availableNewCityTiles: [] as TileType[],  // 新都市タイルは使用しない
  defaultPlayerCount: 1,
  maxTurns: 10,
  hasGoodsDisplay: false,  // Puerto Ricoでは商品ディスプレイを使用しない
  maxSharesLimit: 15,  // 株式発行上限15枚
  isSoloPlay: true,
};

// ============================================================================
// 商品設定
// ============================================================================

/**
 * Puerto Rico 商品キューブ設定（難易度依存）
 * - 赤と黒のみ使用
 * - 難易度によって赤・黒の比率が変動
 * - 合計は常に22個
 *
 * 注: 実際のキューブ数は難易度選択時に DIFFICULTY_CUBES を参照
 */
export const PUERTO_RICO_CUBES_CONFIG = {
  // 使用する色
  usedColors: ['red', 'black'] as const,
  // 除外する色
  excludeColors: ['blue', 'yellow', 'purple'] as const,
  // 合計キューブ数（固定）
  totalCubes: 22,
  // マップ上に配置するキューブ数（11町 × 2個）
  cubesOnMap: 22,
  // 袋に残るキューブ数
  cubesInBag: 0
};

/**
 * Puerto Rico では商品補充表は使用しない
 */
export const PUERTO_RICO_DISPLAY_CONFIG = null;

/**
 * Puerto Rico 初期配置設定
 * - 各町（10町）に2個ずつランダム配置
 * - 都市（SAN JUAN）には配置しない
 */
export const PUERTO_RICO_PLACEMENT_CONFIG = {
  targetLocationType: 'town' as const,  // 町のみに配置
  cubesPerLocation: 2,
  excludeCities: ['SAN JUAN'],
  // 配置対象の町リスト（11町）
  towns: [
    'LUQUILLO',
    'HUMACAO',
    'CAGUAS',
    'BAYAMON',
    'PONCE',
    'ARECIBO',
    'UTUADO',
    'AGUADILLA',
    'MAYAGUEZ',
    'CABO RJO',
    'CAYEY'
  ]
};

// ============================================================================
// ゲーム特有ルール
// ============================================================================

/**
 * Puerto Rico 固有ルール
 */
export const PUERTO_RICO_RULES = {
  /** 初期資金 */
  initialCash: 10,
  /** 初期株式数 */
  initialShares: 2,
  /** 初期機関車レベル */
  initialLocoLevel: 1,
  /** ターンあたりの最大発行可能株式数（制限なし = -1） */
  maxSharesPerTurn: -1,
  /** アクションマーカーの使用 */
  useActionMarkers: false,
  /** アクション選択コスト */
  actionSelectionCost: 5,
  /** 利用可能なアクション */
  availableActions: ['ENGINEER', 'LOCOMOTIVE'] as const,
  /** 商品補充フェーズ */
  hasGoodsRefillPhase: false,
  /** ソロプレイ専用 */
  soloPlayOnly: true
} as const;

// ============================================================================
// スコア計算設定
// ============================================================================

/**
 * Puerto Rico スコア計算設定
 */
export const PUERTO_RICO_SCORING = {
  /** 黒キューブペナルティ: 残留黒キューブ1個につき収入レベル-10 */
  blackCubePenaltyPerCube: 10,
  /** 敗北条件: ペナルティ適用後、収入レベルが0を下回った場合（< 0） */
  defeatIncomeThreshold: 0,
  /** 勝利時スコア計算 */
  victoryScoring: {
    /** 収入トラック × 3 */
    incomeMultiplier: 3,
    /** 線路リンク数 × 1 */
    linkMultiplier: 1,
    /** 発行株式数 × -3 */
    sharesPenaltyMultiplier: -3
  }
} as const;

// ============================================================================
// 難易度設定（型定義から再エクスポート）
// ============================================================================

// DIFFICULTY_CUBES は src/types/index.ts で定義済み
// 使用時は import { DIFFICULTY_CUBES } from '../../../src/types'; を使用

// ============================================================================
// UI設定
// ============================================================================

/**
 * Puerto Rico GameStartModal 設定
 *
 * 共通キーは gameStartCommon から取得、マップ固有キーは gameStartPuertoRico から取得。
 */
export const PUERTO_RICO_START_MODAL_CONFIG = {
  /** i18n マップ固有キープレフィックス */
  i18nKey: 'gameStartPuertoRico',
  /** 勝利条件セクションのスタイル */
  victoryStyle: 'success' as const,
  /** 勝利/終了条件設定（統一構造） */
  victory: {
    gameEndKey: 'gameEnd',  // 共通キー
    additionalItems: ['blackCubePenalty', 'remainingScore'],  // マップ固有キー
  },
  /** 特殊ルールフェーズ */
  phases: [
    { num: '3', ruleKey: 'selectActionsRule', descKey: 'selectActionsDesc', actionsKey: 'selectActionsActions', extraKeys: ['selectActionsCost', 'selectActionsOptional'] },
    { num: '9', ruleKey: 'refillGoodsRule', descKey: 'refillGoodsDesc' },
  ],
  /** 戦略ヒント */
  tips: ['tip1', 'tip2', 'tip3', 'tip4'],
  /** 難易度選択あり */
  hasDifficultySelection: true,
};

/**
 * Puerto Rico GameEndScreen 設定
 */
export const PUERTO_RICO_END_SCREEN_CONFIG = {
  /** スコア計算タイプ */
  scoreType: 'income' as const,
  /** 黒キューブペナルティ */
  blackCubePenalty: 10,
  /** 勝利時スコア倍率 */
  incomeMultiplier: 3,
  sharesPenalty: 3,
  /** 勝利判定閾値（ペナルティ後の収入が0以上で勝利） */
  victoryThreshold: 0,
  /** スコア評価（降順でマッチング、finalScoreベース） */
  scoreEvaluations: [
    { minScore: 61, key: 'score61' },
    { minScore: 41, key: 'score41' },
    { minScore: 21, key: 'score21' },
    { minScore: 1, key: 'score1' },
    { minScore: 0, key: 'score0' },
    { minScore: -Infinity, key: 'defeat' },
  ],
};
